import React, { useEffect, useCallback } from 'react'
import { Typography, useMediaQuery, useTheme } from '@mui/material'
import { StepProps } from '../../../components/_WMS/Stepper'
import { RelocateState } from '../types'
import { RelocateHeader } from './RelocateHeader'
import { BatchSelectionModal } from '../components/BatchSelectionModal'
import { OriginPositionSection } from '../components/OriginPositionSection'
import { QuantitySection } from '../components/QuantitySection'
import { DamageAssessmentCard } from '../components/DamageAssessmentCard'
import { QualityAssignmentLoadingCard } from '../components/QualityAssignmentLoadingCard'
import { DestinyPositionSection } from '../components/DestinyPositionSection'
import { RequiredCommentsCard } from '../components/RequiredCommentsCard'
import { PositionsStepLayout } from '../components/PositionsStepLayout'
import { ActionButtons } from '../components/ActionButtons'
import { MermaModal } from '../components/MermaModal'
import { Batch } from '../../../interfaces/Batch'
import { useModal } from '../../../hooks/useModal'
import { useSettings } from '../../../hooks/useSettings'
import { useFocusManagement } from '../hooks/useFocusManagement'
import { useOriginValidation } from '../hooks/useOriginValidation'
import { useBatchSelection } from '../hooks/useBatchSelection'
import { useQuantityValidation } from '../hooks/useQuantityValidation'
import { useDamageAssessment } from '../hooks/useDamageAssessment'
import { useDestinyValidation } from '../hooks/useDestinyValidation'
import { useResetHandlers } from '../hooks/useResetHandlers'
import { eSetting } from '../../../enums/eSetting'
import { eTypeSetting } from '../../../enums/eTypeSetting'

interface PositionsStepProps extends StepProps<RelocateState> {}

export const PositionsStep: React.FC<PositionsStepProps> = ({
  prev,
  next,
  sharedState,
  setSharedState
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Configuración general: habilita o deshabilita comentarios en reubicación
  const { settingsState, getSettings } = useSettings()
  useEffect(() => {
    getSettings(eTypeSetting.General, null)
  }, [])
  const relocateCommentaryEnabled =
    settingsState.settings.find(
      (s) => s.settingsMetadataId === eSetting.RelocateCommentary
    )?.value === 'true'

  // Focus management hook
  const {
    refs: {
      originInputRef,
      quantityInputRef,
      destinyInputRef,
      documentNumberInputRef
    },
    focusOrigin,
    focusQuantity,
    focusDestiny,
    focusDocumentNumber
  } = useFocusManagement()

  // Origin validation hook
  const originValidation = useOriginValidation({
    sharedState,
    setSharedState
  })

  // Batch selection hook for FEFO logic
  const { shouldShowBatchModal, preparedBatches } = useBatchSelection({
    stockItems: originValidation.stockItems,
    hasBatchManagement: sharedState.hasBatchManagement,
    hasExpirationDate: sharedState.hasExpirationDate
  })

  // Destiny validation hook (declarado primero sin el callback)
  const destinyValidation = useDestinyValidation({
    sharedState,
    setSharedState,
    onValidationSuccess: focusDocumentNumber,
    relocateCommentaryEnabled
  })

  // Damage assessment hook
  const damageAssessment = useDamageAssessment({
    sharedState,
    setSharedState,
    relocateCommentaryEnabled,
    onDamageResponseComplete: (wasAutoAssigned) => {
      mermaModal.closeModal()
      // Solo hacer focus si NO fue auto-asignada (usuario debe ingresar manualmente)
      if (!wasAutoAssigned) {
        focusDestiny()
      }
    },
    onDestinyAutoAssigned: (positionCode) => {
      destinyValidation.setLocalDestinyPosition(positionCode)
      destinyValidation.setDestinyValidated(true)
    },
    onFocusDocumentNumber: () => focusDocumentNumber()
  })

  // Wrapper para handleEditDestiny que también resetea el estado de merma
  const handleEditDestinyWithReset = () => {
    destinyValidation.handleEditDestiny()
    damageAssessment.setIsAutoAssignedPosition(false)
    setSharedState(prev => ({
      ...prev,
      isDamaged: false
    }))
  }

  // Quantity validation hook
  const quantityValidation = useQuantityValidation({
    sharedState,
    setSharedState,
    onQuantityValidated: focusDestiny,
    onShowMermaQuestion: () => {
      if (isMobile) {
        mermaModal.openModal()
      } else {
        damageAssessment.setShowMermaQuestion(true)
      }
    },
    onAutoSendToQuality: damageAssessment.handleMermaResponse
  })

  // Modal para pregunta de merma en mobile
  const mermaModal = useModal('Estado del Producto', false, 'sm')

  // Reset handlers
  const { handleEditOriginWithReset, handleEditQuantityWithReset } =
    useResetHandlers({
      originValidation,
      quantityValidation,
      damageAssessment,
      destinyValidation,
      setSharedState
    })

  // Focus en origen al entrar al step (solo si no está validado)
  useEffect(() => {
    if (!originValidation.originValidated) {
      focusOrigin()
    }
  }, []) // Solo al montar

  // Abrir modal de selección de batch cuando FEFO está activo
  useEffect(() => {
    if (originValidation.originValidated && shouldShowBatchModal && preparedBatches.length > 0) {
      // Si hay solo 1 lote, autoseleccionar sin mostrar modal
      if (preparedBatches.length === 1) {
        setSharedState(prev => ({
          ...prev,
          selectedBatch: preparedBatches[0],
          availableBatches: preparedBatches,
          showBatchModal: false
        }))
        focusQuantity()
        return
      }

      // Si hay múltiples lotes, mostrar modal para selección
      setSharedState(prev => ({
        ...prev,
        availableBatches: preparedBatches,
        showBatchModal: true
      }))
    } else if (originValidation.originValidated && !shouldShowBatchModal) {
      focusQuantity()
    } else if (originValidation.originValidated && shouldShowBatchModal && preparedBatches.length === 0) {
      focusQuantity()
    }
  }, [originValidation.originValidated, shouldShowBatchModal, preparedBatches])

  // Batch handlers
  const handleBatchModal = useCallback(
    (batch?: Batch) => {
      setSharedState((prev) => ({
        ...prev,
        ...(batch && { selectedBatch: batch }),
        showBatchModal: false
      }))
      focusQuantity()
    },
    [setSharedState, focusQuantity]
  )

  // Comment handlers
  const handleDocumentNumberChange = useCallback(
    (value: string) => {
      setSharedState((prev) => ({ ...prev, documentNumber: value }))
    },
    [setSharedState]
  )

  const handleObservationsChange = useCallback(
    (value: string) => {
      setSharedState((prev) => ({ ...prev, observations: value }))
    },
    [setSharedState]
  )

  const canExecuteRelocate = Boolean(
    originValidation.originValidated &&
      quantityValidation.quantityValidated &&
      destinyValidation.destinyValidated &&
      sharedState.cantidad &&
      sharedState.cantidad > 0 &&
      (!sharedState.requiresComments ||
        (sharedState.requiresComments &&
          sharedState.documentNumber?.trim() &&
          sharedState.observations?.trim()))
  )

  return (
    <PositionsStepLayout
      actionButtons={
        <ActionButtons
          canExecuteRelocate={canExecuteRelocate}
          onPrev={prev}
          onNext={next}
          originValidated={originValidation.originValidated}
          quantityValidated={quantityValidation.quantityValidated}
          destinyValidated={destinyValidation.destinyValidated}
          requiresComments={sharedState.requiresComments || false}
          documentNumber={sharedState.documentNumber}
          observations={sharedState.observations}
        />
      }
    >
      <RelocateHeader sharedState={sharedState} />

      {!isMobile && (
        <Typography variant='h6' gutterBottom>
          Posiciones y Cantidad
        </Typography>
      )}

      <React.Fragment>
        {/* Posición Origen */}
        <OriginPositionSection
          originInputRef={originInputRef}
          localOriginPosition={originValidation.localOriginPosition}
          setLocalOriginPosition={originValidation.setLocalOriginPosition}
          originValidated={originValidation.originValidated}
          originValidationFailed={originValidation.originValidationFailed}
          isValidating={originValidation.isValidating}
          error={originValidation.error}
          handleValidateOrigin={originValidation.handleValidateOrigin}
          handleEditOrigin={handleEditOriginWithReset}
          sharedState={sharedState}
        />

        {/* Pregunta de Merma - Solo después de validar cantidad */}
        {damageAssessment.showMermaQuestion &&
          quantityValidation.quantityValidated && (
            <DamageAssessmentCard
              isLoading={damageAssessment.qualityPositionState.loading}
              onMermaResponse={damageAssessment.handleMermaResponse}
            />
          )}

        {/* Cantidad */}
        {originValidation.originValidated &&
          !damageAssessment.showMermaQuestion && (
            <QuantitySection
              quantityInputRef={quantityInputRef}
              localQuantity={quantityValidation.localQuantity}
              handleQuantityChange={quantityValidation.handleQuantityChange}
              quantityValidated={quantityValidation.quantityValidated}
              error={quantityValidation.error}
              handleValidateQuantity={quantityValidation.handleValidateQuantity}
              handleEditQuantity={handleEditQuantityWithReset}
              sharedState={sharedState}
            />
          )}

        {/* Loading de asignación automática a calidad */}
        {originValidation.originValidated &&
          quantityValidation.quantityValidated &&
          !damageAssessment.showMermaQuestion &&
          damageAssessment.qualityPositionState.loading && (
            <QualityAssignmentLoadingCard />
          )}

        {/* Posición Destino */}
        {originValidation.originValidated &&
          quantityValidation.quantityValidated &&
          !damageAssessment.showMermaQuestion &&
          !damageAssessment.qualityPositionState.loading && (
            <DestinyPositionSection
              destinyInputRef={destinyInputRef}
              localDestinyPosition={destinyValidation.localDestinyPosition}
              setLocalDestinyPosition={
                destinyValidation.setLocalDestinyPosition
              }
              destinyValidated={destinyValidation.destinyValidated}
              destinyValidationFailed={
                destinyValidation.destinyValidationFailed
              }
              isValidating={destinyValidation.isValidating}
              isAutoAssignedPosition={damageAssessment.isAutoAssignedPosition}
              autoAssignmentFailed={damageAssessment.autoAssignmentFailed}
              error={destinyValidation.error}
              handleValidateDestiny={destinyValidation.handleValidateDestiny}
              handleEditDestiny={handleEditDestinyWithReset}
              sharedState={sharedState}
            />
          )}

        {/* Campos de Comentarios (si son requeridos) */}
        {destinyValidation.destinyValidated && sharedState.requiresComments && (
          <RequiredCommentsCard
            documentNumberInputRef={documentNumberInputRef}
            documentNumber={sharedState.documentNumber || ''}
            observations={sharedState.observations || ''}
            onDocumentNumberChange={handleDocumentNumberChange}
            onObservationsChange={handleObservationsChange}
          />
        )}
      </React.Fragment>

      {/* Modal de Selección de Lotes */}
      <BatchSelectionModal
        open={sharedState.showBatchModal || false}
        onClose={() => handleBatchModal()}
        onBatchSelect={handleBatchModal}
        batches={sharedState.availableBatches || []}
        sku={sharedState.sku}
        positionCode={sharedState.posicionOrigen}
        hasBatchNumber={sharedState.hasBatchManagement || false}
        hasExpirationDate={sharedState.hasExpirationDate || false}
        isLoading={false}
        skuProductOriginDays={sharedState.expirationDays}
      />

      {/* Modal de Merma para Mobile */}
      <MermaModal
        isOpen={mermaModal.isOpen}
        onClose={mermaModal.closeModal}
        onMermaResponse={damageAssessment.handleMermaResponse}
        isLoading={damageAssessment.qualityPositionState.loading}
      />
    </PositionsStepLayout>
  )
}
